import React, { useEffect, useState } from "react";
import axios from "axios";
import { URLDevelopment } from "../helpers/URL";

const Dashboard = () => {
  // ✅ Get token and cached user from localStorage
  const token = localStorage.getItem("token");
  const cachedUser = JSON.parse(localStorage.getItem("user")) || null;

  const [user, setUser] = useState(
    cachedUser ? { ...cachedUser, addresses: cachedUser.addresses || [] } : null
  );
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Address states
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    _id: "",
    fullName: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
  });

  /* ---------------- FETCH DASHBOARD DATA ---------------- */
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const fetchUserData = async () => {
      try {
        // ✅ Fetch user profile
        const userResponse = await axios.get(
          `/api/user/profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setUser({
          ...userResponse.data,
          addresses: userResponse.data.addresses || [],
        });

        // ✅ Fetch orders
        const ordersResponse = await axios.get(
          `${URLDevelopment}/api/orders/myorders`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setOrders(ordersResponse.data);
      } catch (err) {
        console.error("Dashboard error:", err.response?.data || err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [token]);

  /* ---------------- ADDRESS HANDLERS ---------------- */
  const resetForm = () => {
    setForm({
      _id: "",
      fullName: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    });
    setEditing(false);
    setShowForm(false);
  };

  const handleEdit = (addr) => {
    setForm(addr);
    setEditing(true);
    setShowForm(true);
  };

const handleSave = async () => {
  try {
    if (
      !form.fullName ||
      !form.phone ||
      !form.address ||
      !form.city ||
      !form.state ||
      !form.country ||
      !form.postalCode
    ) {
      return alert("All fields are required");
    }

    const payload = { ...form };
    if (!editing) delete payload._id;

    if (editing) {
      await axios.put(
        `${URLDevelopment}/api/user/address/${form._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } else {
      await axios.post(
        `${URLDevelopment}/api/user/address`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }

    // ✅ Refresh user
    const refreshedUser = await axios.get(
       `${URLDevelopment}/api/user/profile`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const updatedUser = {
      ...refreshedUser.data,
      addresses: refreshedUser.data.addresses || [],
    };

    setUser(updatedUser);

    // ✅ Update localStorage also
    localStorage.setItem("user", JSON.stringify(updatedUser));

    resetForm();
  } catch (err) {
    console.error("Address save error:", err.response?.data || err.message);
  }
};


  const handleDelete = async (id) => {
    if (!window.confirm("Delete this address?")) return;

    try {
      await axios.delete(
        `${URLDevelopment}/api/user/address/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUser((prev) => ({
        ...prev,
        addresses: prev.addresses.filter((a) => a._id !== id),
      }));
    } catch (err) {
      console.error("Delete address error:", err.response?.data || err.message);
    }
  };

  /* ---------------- UI STATES ---------------- */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-blue-600 font-semibold">
        Loading dashboard...
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Please login to view your dashboard.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50 py-10 px-4">
      <div className="max-w-6xl mx-auto space-y-8">

        {/* PROFILE */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h1 className="text-2xl font-bold text-blue-700">
            Welcome, {user.name || "User"}
          </h1>
          <p className="text-gray-600">{user.email}</p>
        </div>

        {/* ADDRESSES */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-blue-700">
              Saved Addresses
            </h2>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg"
            >
              Add Address
            </button>
          </div>

          {user.addresses?.length === 0 && (
            <p className="text-gray-500">No addresses saved.</p>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {user.addresses?.map((addr) => (
              <div key={addr._id} className="border rounded-lg p-4">
                <p className="font-semibold">{addr.fullName}</p>
                <p className="text-sm">{addr.phone}</p>
                <p className="text-sm">
                  {addr.address}, {addr.city}, {addr.state}
                </p>
                <p className="text-sm">
                  {addr.country} - {addr.postalCode}
                </p>

                <div className="flex gap-3 mt-3">
                  <button
                    onClick={() => handleEdit(addr)}
                    className="text-blue-600 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(addr._id)}
                    className="text-red-600 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ORDERS */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-blue-700 mb-4">
            Your Orders
          </h2>

          {orders.length === 0 ? (
            <p className="text-gray-500">You have no orders yet.</p>
          ) : (
            orders.map((order) => (
              <div key={order._id} className="border rounded-lg p-4 mb-4">
                <p className="text-xs text-gray-500">Order ID: {order._id}</p>
                <p className="font-semibold text-blue-600">
                  Total: ₹{order.totalPrice}
                </p>

                <ul className="list-disc list-inside text-sm">
                  {order.orderItems.map((item, idx) => (
                    <li key={idx}>
                      {item.name} × {item.quantity}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ADDRESS MODAL */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-4">
              {editing ? "Edit Address" : "Add Address"}
            </h3>

            <div className="grid grid-cols-2 gap-3">
              {["fullName","phone","city","state","country","postalCode"].map((f) => (
                <input
                  key={f}
                  placeholder={f}
                  className="border px-3 py-2 rounded-lg"
                  value={form[f]}
                  onChange={(e) =>
                    setForm({ ...form, [f]: e.target.value })
                  }
                />
              ))}
              <input
                className="border px-3 py-2 rounded-lg col-span-2"
                placeholder="Address"
                value={form.address}
                onChange={(e) =>
                  setForm({ ...form, address: e.target.value })
                }
              />
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={resetForm} className="border px-4 py-2 rounded-lg">
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
